import { isTokenExpired } from '@/utils/tokenUtils';
import store from '@/state/store';
import { clearToken, clearClerkId, clearProfile } from '@/state/token/clientTokenSlice';

// Get backend URL from environment variable
const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

/**
 * Enhanced fetch wrapper that handles authentication and token expiration
 */
const authenticatedFetch = async (
    input: RequestInfo,
    token: string | undefined,
    init?: RequestInit
): Promise<Response> => {
    // Check if token is expired before making the request
    if (isTokenExpired(token)) {
        console.error('Token is expired, redirecting to login');
        handleLogout();
        throw new Error('Authentication token expired');
    }

    // Prepare headers with authentication
    const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(init?.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
    };

    const newInit: RequestInit = {
        ...init,
        headers
    };

    // Prepend BACKEND_URI if the input is a relative URL (starts with /)
    let url = input;
    if (typeof input === 'string' && input.startsWith('/')) {
        url = `${BACKEND_URI}${input}`;
    }

    try {
        const response = await fetch(url, newInit);

        // Handle 401 and 403 responses
        if (response.status === 401 || response.status === 403) {
            console.error(`Authentication failed with status ${response.status}`);
            
            // Check if it's specifically a token expiration issue
            const responseText = await response.text();
            if (responseText.toLowerCase().includes('expired') || 
                responseText.toLowerCase().includes('unauthorized')) {
                handleLogout();
                throw new Error('Authentication token expired or invalid');
            }
        }

        return response;
    } catch (error) {
        // Re-throw the error for the caller to handle
        throw error;
    }
};

/**
 * Handle logout by clearing all authentication state and redirecting to login
 */
function handleLogout() {
    // Clear Redux state
    store.dispatch(clearToken());
    store.dispatch(clearClerkId());
    store.dispatch(clearProfile());
    
    // Clear any persisted state
    localStorage.removeItem('persist:root');
    
    // Redirect to splash/login page
    window.location.href = '/splash';
}

export { authenticatedFetch };
export default authenticatedFetch;